import { useEffect, useRef } from 'react';
import { useLogger } from '~Hooks';
import { GetPendingOrders_venue_orders } from '~Queries/__queries__';

const MAX_RETRIES = 10;
const RETRY_DELAY_MS = 4000;

interface AutoAcceptOrderProps {
  orderId: string;
  getOrderById: (orderId: string) => Promise<GetPendingOrders_venue_orders | null>;
  acceptOrder: (order: GetPendingOrders_venue_orders) => Promise<void>;
  onAccepted: (orderId: string) => void;
  onGaveUp: (orderId: string) => void;
}

// Each instance of this component handles the auto-accept lifecycle for a single order.
// If this order fails, it retries independently without affecting other orders.
export default function AutoAcceptOrder({
  orderId,
  getOrderById,
  acceptOrder,
  onAccepted,
  onGaveUp,
}: AutoAcceptOrderProps) {
  const logger = useLogger('auto-accept-order');
  const retryCountRef = useRef(0);
  const isProcessingRef = useRef(false);
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    if (hasCompletedRef.current) return;

    const process = async () => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;

      try {
        logger.info('Fetching full order for auto-accept', { orderId });
        const order = await getOrderById(orderId);

        if (!order) {
          logger.warn('Order not found in auto-accept, getOrderById returned null', { orderId });
          hasCompletedRef.current = true;
          onGaveUp(orderId);
          return;
        }

        logger.info(`Trigger auto-accepting for online order ${order.easyId}`, { orderId });

        await acceptOrder(order);

        logger.info(`Successfully auto-accepted order ${order.easyId}`, { orderId });
        hasCompletedRef.current = true;
        onAccepted(orderId);
      } catch (error) {
        retryCountRef.current += 1;
        const retryCount = retryCountRef.current;

        logger.warn(`Failed to auto-accept order, retry ${retryCount}/${MAX_RETRIES}`, {
          orderId,
          errorMsg: error,
        });

        if (retryCount >= MAX_RETRIES) {
          logger.warn('Max accept retries reached for online order', { orderId });
          hasCompletedRef.current = true;
          onGaveUp(orderId);
          return;
        }

        // Schedule a retry
        setTimeout(() => {
          isProcessingRef.current = false;
          process();
        }, RETRY_DELAY_MS);
        return;
      }

      isProcessingRef.current = false;
    };

    process();
  }, []);

  // This component renders nothing - it only handles the auto-accept logic
  return null;
}
